import React, { useRef, useState, useEffect } from "react";
import { Form, Label, Input, Modal, ModalHeader, ModalBody } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { createEventList, getAllCategories, updateVenueEvent } from "../../api";
import { getVenueOrganizerData } from "../../utils/auth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ImageCropper from "../../components/common/ImageCropper/ImageCropper";
import { eventTime } from "../../utils/constant";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { useLocation, useNavigate } from "react-router-dom";

const CreateEventForVenue = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingEvent = (location.state as any)?.event || null;
  const isEditMode = !!editingEvent;
  const passedDate = ((location.state as any)?.date || "") as string;

  const [isLoading, setIsLoading] = useState(false);
  const [eventImage, setEventImage] = useState<File | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    editingEvent?.bannerImgDesktop || editingEvent?.event_image || null
  );

  const [initialValues, setInitialValues] = useState({
    title: "",
    eventDate: passedDate,
    eventTime: "",
    description: "",
    shortDescription: "",
    category: "",
    eventStatus: "active",
    eventUrl: "",
    eventLocation: "",
    eventVenue: "",
    venueCity: "",
    venueState: "",
    venueCountry: "",
    venueZipCode: "",
    venueLat: null as number | null,
    venueLng: null as number | null,
    venueAddressLine1: "",
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getAllCategories();
        setCategories(response.data || response || []);
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };
    fetchCategories();

    if (isEditMode && editingEvent) {
      setInitialValues({
        title: editingEvent.eventName || editingEvent.event_name || "",
        eventDate: editingEvent.eventDate || editingEvent.event_date || "",
        eventTime: editingEvent.eventTime || editingEvent.event_time || "",
        description: editingEvent.eventDescription || editingEvent.event_description || "",
        shortDescription: editingEvent.shortDescription || editingEvent.short_description || "",
        category: String(editingEvent.categoryId || editingEvent.event_category || ""),
        eventStatus: editingEvent.eventStatus || editingEvent.event_status || "active",
        eventUrl: editingEvent.eventUrl || editingEvent.event_url || "",
        eventLocation: editingEvent.eventLocation || editingEvent.event_location || "",
        eventVenue: editingEvent.eventVenue || editingEvent.event_venue || "",
        venueCity: editingEvent.venueCity || editingEvent.venue_city || "",
        venueState: editingEvent.venueState || editingEvent.venue_state || "",
        venueCountry: editingEvent.venueCountry || editingEvent.venue_country || "",
        venueZipCode: editingEvent.venueZipCode || editingEvent.venue_zip_code || "",
        venueLat: editingEvent.venueLat !== undefined ? Number(editingEvent.venueLat) : (editingEvent.venue_lat !== undefined ? Number(editingEvent.venue_lat) : null),
        venueLng: editingEvent.venueLng !== undefined ? Number(editingEvent.venueLng) : (editingEvent.venue_lng !== undefined ? Number(editingEvent.venue_lng) : null),
        venueAddressLine1: editingEvent.venueAddressLine1 || editingEvent.venue_address_line1 || editingEvent.event_location || "",
      });
    } else {
      const venueData = getVenueOrganizerData();
      if (venueData) {
        setInitialValues({
          title: "",
          eventDate: passedDate,
          eventTime: "",
          description: "",
          shortDescription: "",
          category: "",
          eventStatus: "active",
          eventUrl: "",
          // Location UI is hidden — always seed from venue so Yup does not block submit
          eventLocation:
            venueData.address ||
            venueData.name ||
            venueData.venueName ||
            "Venue location",
          eventVenue: venueData.name || venueData.venueName || "",
          venueCity: venueData.city || "",
          venueState: venueData.state || "",
          venueCountry: venueData.country?.countryName || "",
          venueZipCode: venueData.zipcode || "",
          venueLat: venueData.latitude ? Number(venueData.latitude) : null,
          venueLng: venueData.longitude ? Number(venueData.longitude) : null,
          venueAddressLine1: venueData.address || venueData.name || "",
        });
      }
    }
  }, [passedDate, editingEvent, isEditMode]);

  useEffect(() => {
    if (editingEvent) {
      setPreviewUrl(editingEvent.bannerImgDesktop || editingEvent.event_image || null);
    }
  }, [editingEvent]);

  const validationSchema = Yup.object({
    title: Yup.string().required("Event name is required"),
    eventDate: Yup.string()
      .required("Event date is required"),
    eventTime: Yup.string().required("Event time is required"),
    description: Yup.string().required("Event description is required"),
    shortDescription: Yup.string().max(120),
    category: Yup.string().required("Category is required"),
    eventStatus: Yup.string().required("Event status is required"),
    // Optional — empty OK; if set must be https://
    eventUrl: Yup.string().matches(/^$|^https:\/\/.+/, "URL must start with https://"),
    eventLocation: Yup.string().required("Event location is required"),
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    // Don't block the button on isValid alone — hidden location field was failing silently
    validateOnMount: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        const venueData = getVenueOrganizerData();
        const fd = new FormData();
        fd.append("event_name", values.title);
        fd.append("event_description", values.description);
        fd.append("event_date", values.eventDate);
        fd.append("event_time", values.eventTime.split(':').length === 2 ? `${values.eventTime}:00` : values.eventTime);
        fd.append("event_category", values.category);
        fd.append("event_status", values.eventStatus);
        fd.append("is_active", values.eventStatus === "active" ? "true" : "false");
        fd.append("event_created_by", venueData?.userId || editingEvent?.event_created_by);
        // Organizer label = venue display name (not the event title)
        fd.append(
          "organizer_name",
          venueData?.venueName ||
            venueData?.name ||
            venueData?.userName ||
            editingEvent?.organizer_name ||
            ""
        );
        fd.append("country_id", venueData?.location || editingEvent?.country_id);
        if (values.eventUrl) {
          fd.append("event_url", values.eventUrl);
        }
        if (eventImage) {
          fd.append("file", eventImage);
        }

        const locationData = {
          name: values.eventVenue || values.eventLocation,
          addressLine1: values.venueAddressLine1 || values.eventLocation,
          addressLine2: "",
          city: values.venueCity,
          state: values.venueState,
          country: venueData?.location,
          zipCode: values.venueZipCode,
          latitude: values.venueLat,
          longitude: values.venueLng,
          formattedAddress: values.eventLocation,
        };
        // Also set flat event_location so schedule/list UIs can show it without parsing JSON
        fd.append(
          "event_location",
          values.venueAddressLine1 || values.eventLocation || values.eventVenue || ""
        );
        fd.append("locationData", JSON.stringify([locationData]));

        if (isEditMode) {
          const eventId = editingEvent.eventId || editingEvent.event_id;
          await updateVenueEvent(eventId, fd);
          toast.success("Event updated successfully");
          setTimeout(() => {
            navigate("/venue-events");
          }, 1500);
        } else {
          await createEventList(fd);
          resetForm();
          setEventImage(null);
          setSelectedImage(null);
          setPreviewUrl(null);
          toast.success("Event created successfully");
          // Same as edit — leave create form and show the new event in the list
          setTimeout(() => {
            navigate("/venue-events");
          }, 1500);
        }
      } catch (e) {
        console.error(e);
        toast.error(isEditMode ? "Failed to update event" : "Failed to create event");
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div className="event-dt-block p-10">
      <ToastContainer />
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-8 col-lg-9 col-md-12">
            <div className="main-title text-center mb-4">
              <h3>{isEditMode ? "Edit Venue Event" : "Create Venue Event"}</h3>
            </div>

            <div className="main-card">
              <div className="bp-title">
                <h4>
                  <i className="fa-solid fa-circle-info step_icon me-3"></i>
                  Event Details
                </h4>
              </div>

              <div className="bp-form main-form p-3">
                <Form onSubmit={formik.handleSubmit}>
                  <div className="form-group border_bottom pb_30">
                    <Label className="form-label fs-16">
                      Event Name *
                    </Label>
                    <Input
                      className="form-control h_50"
                      name="title"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.title}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <div className="text-danger">{formik.errors.title}</div>
                    )}
                  </div>

                  <div className="form-group border_bottom pt_30 pb_30">
                    <Label className="form-label fs-16">
                      When is your event?*
                    </Label>

                    <div className="row g-2">
                      <div className="col-md-6 col-sm-6">
                        <Label className="form-label mt-3 fs-6">
                          Event Date*
                        </Label>
                        {/* Native date input — Flatpickr conflicted with dark theme and blocked picking days */}
                        <Input
                          type="date"
                          className="form-control h_50"
                          name="eventDate"
                          value={formik.values.eventDate || ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          style={{ colorScheme: "dark" }}
                        />
                        {formik.touched.eventDate && formik.errors.eventDate && (
                          <div className="text-danger">
                            {formik.errors.eventDate}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 col-sm-6">
                        <Label className="form-label mt-3 fs-6">
                          Event Time*
                        </Label>
                        <Input
                          type="select"
                          className="form-select h_50"
                          name="eventTime"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.eventTime}
                        >
                          <option value="">Select start time</option>
                          {eventTime.map((time) => (
                            <option key={time.value} value={time.value}>
                              {time.label}
                            </option>
                          ))}
                        </Input>
                        {formik.touched.eventTime && formik.errors.eventTime && (
                          <div className="text-danger">
                            {formik.errors.eventTime}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-group border_bottom pt_30 pb_30">
                    <Label className="form-label fs-16">
                      Event Category *
                    </Label>
                    <Input
                      type="select"
                      className="form-select h_50"
                      name="category"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.category}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.categoryId} value={cat.categoryId}>
                          {cat.categoryName}
                        </option>
                      ))}
                    </Input>
                    {formik.touched.category && formik.errors.category && (
                      <div className="text-danger">{formik.errors.category}</div>
                    )}
                  </div>

                  <div className="form-group border_bottom pt_30 pb_30">
                    <Label className="form-label fs-16">
                      Short Description *
                    </Label>
                    <Input
                      type="textarea"
                      className="form-control"
                      rows={4}
                      name="description"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.description}
                    />
                    {formik.touched.description && formik.errors.description && (
                      <div className="text-danger">{formik.errors.description}</div>
                    )}
                  </div>

                  {/* <div className="form-group border_bottom pt_30 pb_30">
                    <Label className="form-label fs-16">
                      Short Description (optional)
                    </Label>
                    <Input
                      className="form-control h_50"
                      name="shortDescription"
                      maxLength={120}
                      onChange={formik.handleChange}
                      value={formik.values.shortDescription}
                    />
                    <small className="text-muted">
                      {formik.values.shortDescription.length}/120
                    </small>
                  </div> */}

                  <div className="form-group border_bottom pt_30 pb_30">
                    <Label className="form-label fs-16">
                      Event Status *
                    </Label>
                    <Input
                      type="select"
                      className="form-select h_50"
                      name="eventStatus"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.eventStatus}
                    >
                      <option value="active">Active</option>
                      <option value="draft">Draft</option>
                      <option value="inactive">Inactive</option>
                      <option value="closed">Closed</option>
                    </Input>
                    {formik.touched.eventStatus && formik.errors.eventStatus && (
                      <div className="text-danger">{formik.errors.eventStatus}</div>
                    )}
                  </div>

                  <div className="form-group border_bottom pt_30 pb_30" style={{ display: "none" }}>
                    <Label className="form-label fs-16">
                      Event Location *
                    </Label>
                    <div className="google-places-container">
                      <GooglePlacesAutocomplete
                        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
                        selectProps={{
                          placeholder: "Search for venue location...",
                          isClearable: false,
                          isDisabled: true,
                          value: formik.values.eventLocation
                            ? {
                                label: formik.values.eventLocation,
                                value: {
                                  description: formik.values.venueAddressLine1,
                                },
                              }
                            : null,
                          onChange: (place: any) => {
                            if (!place) {
                              formik.setFieldValue("eventLocation", "");
                              formik.setFieldValue("eventVenue", "");
                              formik.setFieldValue("venueCity", "");
                              formik.setFieldValue("venueState", "");
                              formik.setFieldValue("venueCountry", "");
                              formik.setFieldValue("venueZipCode", "");
                              formik.setFieldValue("venueLat", null);
                              formik.setFieldValue("venueLng", null);
                              formik.setFieldValue("venueAddressLine1", "");
                              return;
                            }

                            formik.setFieldValue("eventLocation", place.label || "");
                            formik.setFieldValue("eventVenue", place.label || "");
                            formik.setFieldValue("venueAddressLine1", place.value?.description || "");
                            formik.setFieldValue("venueLat", place.value?.geometry?.location?.lat || null);
                            formik.setFieldValue("venueLng", place.value?.geometry?.location?.lng || null);

                            if (!place.value?.place_id) return;

                            const geocoder = new google.maps.Geocoder();
                            geocoder.geocode(
                              { placeId: place.value.place_id },
                              (results, status) => {
                                if (status !== "OK" || !results?.[0]) return;

                                const result = results[0];
                                formik.setFieldValue("venueAddressLine1", result.formatted_address || "");
                                formik.setFieldValue("venueLat", result.geometry.location.lat());
                                formik.setFieldValue("venueLng", result.geometry.location.lng());

                                result.address_components.forEach((component) => {
                                  const types = component.types;
                                  if (types.includes("country")) {
                                    formik.setFieldValue("venueCountry", component.long_name);
                                  }
                                  if (types.includes("administrative_area_level_1")) {
                                    formik.setFieldValue("venueState", component.long_name);
                                  }
                                  if (types.includes("locality")) {
                                    formik.setFieldValue("venueCity", component.long_name);
                                  }
                                  if (types.includes("postal_code")) {
                                    formik.setFieldValue("venueZipCode", component.long_name);
                                  }
                                });
                              }
                            );
                          },
                          styles: {
                            control: (provided: any, state: any) => ({
                              ...provided,
                              minHeight: "50px",
                              backgroundColor: "transparent",
                              border: state.isFocused
                                ? "2px solid #ee3f7c"
                                : "1px solid #ced4da",
                              borderRadius: "4px",
                              boxShadow: state.isFocused
                                ? "0 0 0 0.2rem rgba(238, 63, 124, 0.15)"
                                : "none",
                              "&:hover": {
                                borderColor: "#ee3f7c",
                              },
                            }),
                            placeholder: (provided: any) => ({
                              ...provided,
                              color: "#6c757d",
                              fontSize: "14px",
                            }),
                            input: (provided: any) => ({
                              ...provided,
                              fontSize: "14px",
                              padding: "8px 0",
                            }),
                            menu: (provided: any) => ({
                              ...provided,
                              borderRadius: "4px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              border: "1px solid #e9ecef",
                              zIndex: 9999,
                            }),
                            option: (provided: any, state: any) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#ee3f7c"
                                : state.isFocused
                                ? "rgba(0,0,0,0.05)"
                                : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "12px 16px",
                              fontSize: "14px",
                            }),
                            singleValue: (provided: any) => ({
                              ...provided,
                              fontSize: "14px",
                              color: "#495057",
                            }),
                          },
                        }}
                      />
                    </div>
                    {formik.touched.eventLocation && formik.errors.eventLocation && (
                      <div className="text-danger mt-1">{formik.errors.eventLocation}</div>
                    )}
                  </div>

                  <div className="form-group border_bottom pt_30 pb_30">
                    <Label className="form-label fs-16">
                      Event URL (optional)
                    </Label>
                    <Input
                      className="form-control h_50"
                      name="eventUrl"
                      placeholder="https://..."
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.eventUrl}
                    />
                    {formik.touched.eventUrl && formik.errors.eventUrl && (
                      <div className="text-danger">{formik.errors.eventUrl}</div>
                    )}
                  </div>

                  <div className="form-group border_bottom pt_30 pb_30">
                    <Label className="form-label fs-16">
                      Event Image
                    </Label>

                    <p className="mt-2 fs-14 d-block mb-3 pe_right">
                      Upload image
                    </p>

                    <div className="content-holder mt-4" style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "flex-start" }}>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/png,image/jpeg"
                              style={{ display: "none" }}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (file.size > 3 * 1024 * 1024) {
                                  toast.error("Image must be less than 3MB");
                                  return;
                                }
                                setSelectedImage(file);
                                setShowCropper(true);
                              }}
                            />
                      <div
                        style={{
                          width: "240px",
                          aspectRatio: "3/4",
                          border: "2px dashed #ccc",
                          borderRadius: "4px",
                          backgroundColor: "#f8f9fa",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "10px",
                          overflow: "hidden",
                          cursor: "pointer",
                        }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {previewUrl ? (
                          <img
                            src={previewUrl}
                            alt="preview"
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ee3f7c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="16 16 12 12 8 16" />
                              <line x1="12" y1="12" x2="12" y2="21" />
                              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                            </svg>
                            <span style={{ fontSize: "12px", color: "#999" }}>3:4 Aspect Ratio</span>
                          </>
                        )}
                      </div>

                      <button
                        type="button"
                        className="btn"
                        style={{
                          backgroundColor: "#ee3f7c",
                          color: "#fff",
                          borderRadius: "6px",
                          padding: "8px 20px",
                          fontWeight: 500,
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          border: "none",
                        }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                        </svg>
                        {previewUrl ? "Change Image" : "Upload Image"}
                      </button>
                    </div>
                  </div>

                  <div className="pt_20">
                    <button
                      type="submit"
                      className="gradient-button w-100 h_50"
                      disabled={isLoading}
                      onClick={() => {
                        // Surface all field errors when user clicks Create while invalid
                        if (!formik.isValid) {
                          formik.setTouched(
                            Object.keys(formik.values).reduce(
                              (acc, key) => ({ ...acc, [key]: true }),
                              {} as Record<string, boolean>
                            )
                          );
                        }
                      }}
                    >
                      <h5 className="mb-0">
                        {isLoading
                          ? (isEditMode ? "Updating Event..." : "Creating Event...")
                          : (isEditMode ? "Update Event" : "Create Event")}
                      </h5>
                    </button>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCropper && selectedImage && (
            <ImageCropper
              imageFile={selectedImage}
              onCropComplete={(croppedFile) => {
                setEventImage(croppedFile);
                setPreviewUrl(URL.createObjectURL(croppedFile));
                setShowCropper(false);
              }}
          onCancel={() => setShowCropper(false)}
          aspect={3 / 4}
        />
      )}
    </div>
  );
};

export default CreateEventForVenue;